import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import Layout from '../components/Layout';
import FeedTabs from '../components/FeedTabs';
import FeedCard from '../components/FeedCard';
import { getForYouFeed, getFollowingFeed, getExploreFeed, getCommunityFeed } from '../services/feedApi';
import { Loader, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

/**
 * TestFeed Page - High-Performance, Engagement-Optimized Feed
 * 
 * Features:
 * - Smooth tab transitions with fade effect
 * - Optimistic UI updates
 * - Event tracking for ML
 * - Performance optimizations (containment, debouncing)
 * - Debug mode for ranking visibility
 */

const TestFeed = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('for-you');
  const [feedItems, setFeedItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Initialize debug mode from URL parameter
  const [debugMode, setDebugMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('debug') === 'true';
  });
  
  const observerTarget = useRef(null);
  const impressionLogRef = useRef(new Set());
  const fetchingRef = useRef(false); // Prevent concurrent fetches
  const lastFetchTimeRef = useRef(0); // Rate limiting

  // Fetch feed based on active tab
  const fetchFeed = useCallback(async (pageNum = 1, append = false) => {
    // Prevent concurrent fetches
    if (fetchingRef.current) {
      console.log('⏸️ Fetch already in progress, skipping...');
      return;
    }

    // Rate limiting - prevent too frequent requests
    const now = Date.now();
    if (append && now - lastFetchTimeRef.current < 500) {
      console.log('⏸️ Rate limited, skipping fetch...');
      return;
    }

    try {
      fetchingRef.current = true;
      lastFetchTimeRef.current = now;

      if (pageNum === 1) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      let response;
      // Pass debug mode to API if enabled
      const apiDebugMode = debugMode;
      
      switch (activeTab) {
        case 'for-you':
          response = await getForYouFeed(pageNum, 20, apiDebugMode);
          break;
        case 'following':
          response = await getFollowingFeed(pageNum, 20, apiDebugMode);
          break;
        case 'explore':
          response = await getExploreFeed(pageNum, 20, apiDebugMode);
          break;
        case 'community':
          response = await getCommunityFeed(pageNum, 20, apiDebugMode);
          break;
        default:
          response = await getForYouFeed(pageNum, 20, apiDebugMode);
      }

      const newItems = response.items || [];
      
      if (append && newItems.length > 0) {
        setFeedItems(prev => {
          // Prevent duplicates
          const existingIds = new Set(prev.map(item => item.id));
          const uniqueNew = newItems.filter(item => !existingIds.has(item.id));
          return [...prev, ...uniqueNew];
        });
      } else if (!append) {
        setFeedItems(newItems);
        impressionLogRef.current.clear(); // Reset impression log on new feed
      }
      
      setHasMore(response.has_more || false);
      
      // Handle empty state messages
      if (newItems.length === 0 && pageNum === 1) {
        setError(response.message || getEmptyStateMessage(activeTab));
      }
      
    } catch (err) {
      console.error('Error fetching feed:', err);
      if (pageNum === 1) {
        setError('Failed to load feed. Please check your connection and try again.');
      } else {
        // Don't show error toast for pagination failures on mobile
        console.warn('Failed to load more items silently');
        setHasMore(false); // Stop trying to load more
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
      fetchingRef.current = false;
    }
  }, [activeTab, debugMode]);

  // Enhanced empty state messages
  const getEmptyStateMessage = (tab) => {
    const messages = {
      'for-you': user 
        ? 'No content yet. Follow topics and users to personalize your feed!' 
        : 'No content available. Please check back soon.',
      'following': user
        ? 'Follow users or topics to see content here. Start by browsing popular topics!'
        : 'Please login to see content from users and topics you follow.',
      'explore': 'No content available yet. Check back soon or try another tab!',
      'community': 'No trending content right now. Check back later for hot discussions!'
    };
    return messages[tab] || 'No content to show.';
  };

  // Get empty state suggestions
  const getEmptyStateSuggestions = (tab) => {
    if (!user && (tab === 'for-you' || tab === 'following')) {
      return [
        { text: 'Login', action: '/login', emoji: '🔑' },
        { text: 'Browse Topics', action: '/topics', emoji: '📑' }
      ];
    }

    const suggestions = {
      'for-you': [
        { text: 'Follow Topics', action: '/topics', emoji: '📖' },
        { text: 'Ask a Question', action: '/ask', emoji: '❓' }
      ],
      'following': [
        { text: 'Find Topics', action: '/topics', emoji: '🔍' },
        { text: 'Explore Feed', action: '#', onClick: () => setActiveTab('explore'), emoji: '🌍' }
      ],
      'explore': [
        { text: 'Ask a Question', action: '/ask', emoji: '📝' },
        { text: 'For You Feed', action: '#', onClick: () => setActiveTab('for-you'), emoji: '✨' }
      ],
      'community': [
        { text: 'Explore Feed', action: '#', onClick: () => setActiveTab('explore'), emoji: '🔥' },
        { text: 'All Topics', action: '/topics', emoji: '📚' }
      ]
    };

    return suggestions[tab] || [];
  };

  // Load feed when tab changes or component mounts
  useEffect(() => {
    setPage(1);
    fetchFeed(1, false);
  }, [activeTab, fetchFeed]);

  // Infinite scroll - intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchFeed(nextPage, true);
        }
      },
      { threshold: 0.5 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, loadingMore, page, fetchFeed]);

  // Handle tab change with smooth transition
  const handleTabChange = (tabId) => {
    if (tabId === activeTab || isTransitioning) return;
    
    // Trigger fade-out
    setIsTransitioning(true);
    
    // Wait for fade-out, then change tab
    setTimeout(() => {
      setActiveTab(tabId);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Fade back in
      setTimeout(() => setIsTransitioning(false), 50);
    }, 150);
    
    logEvent('tab_change', { from: activeTab, to: tabId });
  };

  // Event logging (for future ML)
  const logEvent = (eventType, data) => {
    const event = {
      type: eventType,
      timestamp: new Date().toISOString(),
      user_id: user?.id || 'anonymous',
      tab: activeTab,
      ...data
    };
    console.log('[Feed Event]', event);
    // TODO: Send to analytics endpoint
  };

  // Handle impression
  const handleImpression = (item) => {
    const key = `${item.content_type}-${item.id}`;
    if (!impressionLogRef.current.has(key)) {
      impressionLogRef.current.add(key);
      logEvent('impression', {
        content_id: item.id,
        content_type: item.content_type,
        feed_score: item.feed_score,
        position: feedItems.indexOf(item) + 1
      });
    }
  };

  // Handle click
  const handleClick = (item) => {
    logEvent('click', {
      content_id: item.id,
      content_type: item.content_type,
      feed_score: item.feed_score,
      position: feedItems.indexOf(item) + 1
    });
  };

  // Handle like
  const handleLike = (item) => {
    logEvent('like', {
      content_id: item.id,
      content_type: item.content_type
    });
    toast.success('Like feature coming soon!');
  };

  // Handle dislike
  const handleDislike = (item) => {
    logEvent('dislike', {
      content_id: item.id,
      content_type: item.content_type
    });
    toast.success('Dislike feature coming soon!');
  };

  // Handle share
  const handleShare = async (item) => {
    const url = item.content_type === 'question' 
      ? `${window.location.origin}/questions/${item.slug}`
      : `${window.location.origin}/questions/${item.question?.slug}#answer-${item.id}`;
    
    try {
      if (navigator.share) {
        await navigator.share({
          title: item.title || item.question?.title || 'Check this out',
          url: url
        });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard!');
      }
      logEvent('share', {
        content_id: item.id,
        content_type: item.content_type,
        method: navigator.share ? 'native' : 'clipboard'
      });
    } catch (err) {
      console.error('Error sharing:', err);
    }
  };

  return (
    <Layout>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>WeboAsk - Ask Questions, Get Answers, Learn Together</title>
        <meta 
          name="description" 
          content="Join WeboAsk community to ask questions, share knowledge, and learn from experts. Explore topics in Technology, Science, Health, Business, and more." 
        />
        <link rel="canonical" href="https://weboask.com" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://weboask.com" />
        <meta property="og:title" content="WeboAsk - Community Q&A Platform" />
        <meta property="og:description" content="Ask the web. Learn from people. A community-driven platform for knowledge sharing." />
        <meta property="og:site_name" content="WeboAsk" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://weboask.com" />
        <meta property="twitter:title" content="WeboAsk - Q&A Community" />
        <meta property="twitter:description" content="Ask questions, get answers, share knowledge with the community" />
        
        {/* Indexing */}
        <meta name="robots" content="index, follow" />
        
        {/* WebSite Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "WeboAsk",
            "description": "Community Q&A platform where curiosity meets knowledge",
            "url": "https://weboask.com",
            "potentialAction": {
              "@type": "SearchAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": "https://weboask.com/search?q={search_term_string}"
              },
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Feed Tabs */}
        <FeedTabs activeTab={activeTab} onTabChange={handleTabChange} />

        {/* Debug Mode Toggle - Better spacing */}
        <div className="max-w-4xl mx-auto px-4 pt-5 pb-1">
          <div className="flex items-center gap-3">
            <button
              data-testid="debug-toggle-button"
              onClick={() => setDebugMode(!debugMode)}
              className="flex items-center gap-2 px-3 py-2 text-xs bg-yellow-100 hover:bg-yellow-200 text-yellow-900 rounded-lg transition-colors border border-yellow-300"
            >
              <Settings className="w-4 h-4" />
              <span>{debugMode ? 'Hide' : 'Show'} Debug Info</span>
            </button>
            {debugMode && (
              <span className="text-xs text-gray-600">
                💡 Tip: Share debug mode with <code className="bg-gray-100 px-1 py-0.5 rounded">?debug=true</code>
              </span>
            )}
          </div>
        </div>

        {/* Feed Content - Smooth transitions */}
        <div 
          className={`max-w-4xl mx-auto px-4 py-5 space-y-4 transition-opacity duration-150 ${
            isTransitioning ? 'opacity-0' : 'opacity-100'
          }`}
          style={{ contain: 'layout style paint' }}
        >
          {/* Loading State */}
          {loading && (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <FeedCardSkeleton key={i} />
              ))}
            </div>
          )}

          {/* Error/Empty State - Enhanced with Retry */}
          {!loading && error && (
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center space-y-4">
              <div className="text-6xl mb-2">
                {activeTab === 'following' ? '👥' : activeTab === 'community' ? '🔥' : '💭'}
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-gray-900">
                  {activeTab === 'following' ? 'No content yet' : activeTab === 'community' ? 'No trending content' : 'Nothing to show'}
                </h3>
                <p className="text-gray-600 text-sm max-w-md mx-auto leading-relaxed">
                  {error}
                </p>
              </div>
              
              {/* Retry Button for Connection Errors */}
              {error.includes('Failed to load') && (
                <button
                  onClick={() => {
                    setError(null);
                    setPage(1);
                    fetchFeed(1, false);
                  }}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
                >
                  <span>🔄</span>
                  <span>Try Again</span>
                </button>
              )}

              {/* Suggestions */}
              {!error.includes('Failed to load') && getEmptyStateSuggestions(activeTab).length > 0 && (
                <div className="flex flex-wrap justify-center gap-3 pt-4">
                  {getEmptyStateSuggestions(activeTab).map((suggestion, idx) => (
                    suggestion.onClick ? (
                      <button
                        key={idx}
                        onClick={suggestion.onClick}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
                      >
                        <span>{suggestion.emoji}</span>
                        <span>{suggestion.text}</span>
                      </button>
                    ) : (
                      <a
                        key={idx}
                        href={suggestion.action}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
                      >
                        <span>{suggestion.emoji}</span>
                        <span>{suggestion.text}</span>
                      </a>
                    )
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Feed Items */}
          {!loading && feedItems.length > 0 && (
            <>
              {feedItems.map((item) => (
                <FeedCard
                  key={`${item.content_type}-${item.id}`}
                  item={item}
                  onLike={handleLike}
                  onDislike={handleDislike}
                  onShare={handleShare}
                  onImpression={handleImpression}
                  onClick={handleClick}
                  debugMode={debugMode}
                />
              ))}
            </>
          )}

          {/* Loading More Indicator */}
          {loadingMore && (
            <div className="flex justify-center py-8">
              <Loader className="w-6 h-6 text-purple-600 animate-spin" />
            </div>
          )}

          {/* Infinite Scroll Trigger */}
          {hasMore && !loading && (
            <div ref={observerTarget} className="h-20" />
          )}

          {/* End of Feed */}
          {!loading && !hasMore && feedItems.length > 0 && (
            <div className="text-center py-8 space-y-2">
              <p className="text-gray-500 text-sm">You've reached the end of the feed</p>
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-purple-600 hover:text-purple-700 font-medium text-sm"
              >
                Scroll to top
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

// Skeleton loading component - 16px spacing
const FeedCardSkeleton = () => {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 animate-pulse space-y-4">
      {/* Header skeleton */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gray-200" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-32" />
          <div className="h-3 bg-gray-200 rounded w-24" />
        </div>
      </div>
      
      {/* Content skeleton */}
      <div className="space-y-2">
        <div className="h-4 bg-gray-200 rounded w-full" />
        <div className="h-4 bg-gray-200 rounded w-5/6" />
        <div className="h-4 bg-gray-200 rounded w-4/6" />
      </div>
      
      {/* Actions skeleton */}
      <div className="flex items-center gap-4 pt-3 border-t border-gray-100">
        <div className="h-8 bg-gray-200 rounded w-16" />
        <div className="h-8 bg-gray-200 rounded w-16" />
        <div className="h-8 bg-gray-200 rounded w-16" />
      </div>
    </div>
  );
};

export default TestFeed;
